import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CreateRestaurantPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    address: "",
    subscription_plan: "commission",
    commission_rate: 5.0, // Default 5% commission
  });
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    subdomain: "",
    restaurantName: "",
  });

  const generateSubdomain = (name) => {
    // Add random number to ensure uniqueness
    const randomSuffix = Math.floor(Math.random() * 999) + 1;
    return (
      name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
        .substring(0, 15) + randomSuffix
    );
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      subdomain: generateSubdomain(name),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Generate password
    const password = generatePassword();

    try {
      // Create restaurant
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/restaurants`,
        formData,
        { headers: getAdminAuthHeader() }
      );

      const restaurant = response.data;

      // Create restaurant admin user in their database
      await axios.post(
        `${BACKEND_URL}/api/admin/restaurants/${restaurant.id}/initialize`,
        {
          admin_email: formData.owner_email,
          admin_password: password,
          admin_name: formData.owner_name,
        },
        { headers: getAdminAuthHeader() }
      );

      // Set credentials and show modal
      setCredentials({
        email: formData.owner_email,
        password: password,
        subdomain: restaurant.subdomain,
        restaurantName: formData.name,
      });
      setShowCredentialsModal(true);
      setLoading(false);
    } catch (error) {
      console.error("Error creating restaurant:", error);
      const errorMsg =
        error.response?.data?.detail || "Failed to create restaurant";
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success(`${label} copied to clipboard!`);
        })
        .catch(() => {
          // Fallback for clipboard API failure
          copyFallback(text);
        });
    } else {
      copyFallback(text);
    }

    function copyFallback(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success(`${label} copied to clipboard!`);
      } catch (err) {
        toast.error("Failed to copy");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCloseModal = () => {
    setShowCredentialsModal(false);
    navigate("/admin/dashboard");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/dashboard")}
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Create New Restaurant
              </h1>
              <p className="text-sm text-gray-600">
                Onboard a new restaurant to WaiterMan POS
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Restaurant Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Restaurant Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">
                  Basic Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Restaurant Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={handleNameChange}
                      placeholder="e.g., Taste of India"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="subdomain">
                      Subdomain (Must be unique) *
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="subdomain"
                        value={formData.subdomain}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subdomain: e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9]/g, ""),
                          })
                        }
                        placeholder="tasteofindia123"
                        required
                        className="font-mono"
                      />
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        .waiterman.com
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      🔗 QR ordering URL:{" "}
                      <span className="font-semibold">
                        {formData.subdomain || "[subdomain]"}.waiterman.com
                      </span>
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Must be unique - will show error if already taken
                    </p>
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="address">Full Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      placeholder="123 Main St, Sydney, NSW 2000"
                    />
                  </div>
                </div>
              </div>

              {/* Owner Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">
                  Owner / Contact Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      value={formData.owner_name}
                      onChange={(e) =>
                        setFormData({ ...formData, owner_name: e.target.value })
                      }
                      placeholder="John Smith"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner_phone">Phone Number *</Label>
                    <Input
                      id="owner_phone"
                      type="tel"
                      value={formData.owner_phone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          owner_phone: e.target.value,
                        })
                      }
                      placeholder="+61 400 123 456"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="owner_email">
                      Email Address (Login Username) *
                    </Label>
                    <Input
                      id="owner_email"
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          owner_email: e.target.value,
                        })
                      }
                      placeholder="owner@restaurant.com"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will be used as login username
                    </p>
                  </div>
                </div>
              </div>

              {/* Commission & Pricing */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">
                  Commission & Pricing
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="plan">Pricing Model *</Label>
                    <Select
                      value={formData.subscription_plan}
                      onValueChange={(value) =>
                        setFormData({ ...formData, subscription_plan: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="commission">
                          Commission-Based
                        </SelectItem>
                        <SelectItem value="custom">Custom Pricing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="commission_rate">
                      Commission Rate (%) *
                    </Label>
                    <Input
                      id="commission_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          commission_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      placeholder="5.0"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      % of total billing charged as commission
                    </p>
                  </div>
                </div>

                {/* Commission Calculator */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">
                    💰 Commission Calculator
                  </h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>
                      • If restaurant bills <strong>$10,000/month</strong>
                    </p>
                    <p>
                      • With{" "}
                      <strong>{formData.commission_rate}% commission</strong>
                    </p>
                    <p>
                      • You earn:{" "}
                      <strong className="text-lg">
                        ${((10000 * formData.commission_rate) / 100).toFixed(2)}
                        /month
                      </strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  📋 What happens next?
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Restaurant profile will be created</li>
                  <li>
                    ✓ Unique login credentials will be generated automatically
                  </li>
                  <li>✓ Restaurant database will be initialized</li>
                  <li>
                    ✓ You'll receive credentials to share with the restaurant
                  </li>
                  <li>✓ They can login and start adding menu, tables, staff</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/dashboard")}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 flex-1"
                  disabled={loading}
                >
                  {loading
                    ? "Creating Restaurant..."
                    : "Create Restaurant & Generate Credentials"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Credentials Modal */}
        <Dialog
          open={showCredentialsModal}
          onOpenChange={setShowCredentialsModal}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <DialogTitle className="text-2xl">
                    Restaurant Created Successfully!
                  </DialogTitle>
                  <DialogDescription>
                    Save these credentials and share with the restaurant owner
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Restaurant Name */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">
                  {credentials.restaurantName}
                </h3>
                <p className="text-sm text-gray-600">
                  Subdomain:{" "}
                  <span className="font-mono font-semibold">
                    {credentials.subdomain}.waiterman.com
                  </span>
                </p>
              </div>

              {/* Login Credentials */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">
                  Login Credentials
                </h4>

                {/* Email */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Email Address
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                      {credentials.email}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        copyToClipboard(credentials.email, "Email")
                      }
                    >
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>

                {/* Password */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Password (Auto-generated)
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                      {credentials.password}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        copyToClipboard(credentials.password, "Password")
                      }
                    >
                      <Copy size={16} />
                    </Button>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    ⚠️ This password will NOT be shown again. Please save it
                    securely.
                  </p>
                </div>

                {/* Login URL */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Login URL
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                      {BACKEND_URL.replace("/api", "")}/login
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        copyToClipboard(
                          `${BACKEND_URL.replace("/api", "")}/login`,
                          "URL"
                        )
                      }
                    >
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">
                  📋 Next Steps
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>✓ Restaurant profile created</li>
                  <li>✓ Database initialized with default settings</li>
                  <li>✓ Admin user created with above credentials</li>
                  <li>→ Share these credentials with the restaurant owner</li>
                  <li>
                    → They can login and start configuring their POS system
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  const text = `Restaurant: ${
                    credentials.restaurantName
                  }\nEmail: ${credentials.email}\nPassword: ${
                    credentials.password
                  }\nLogin URL: ${BACKEND_URL.replace(
                    "/api",
                    ""
                  )}/login\nSubdomain: ${credentials.subdomain}.waiterman.com`;
                  copyToClipboard(text, "All credentials");
                }}
                className="flex-1"
              >
                <Copy size={16} className="mr-2" />
                Copy All Credentials
              </Button>
              <Button
                onClick={handleCloseModal}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                ✓ Done - Go to Dashboard
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
